import { HttpErrorResponse } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { combineLatest, EMPTY, from, Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { CreateIntentResponse } from 'src/app/services/payment.service';
import { SlipService } from 'src/app/services/slip.service';
import { ParentHubStripe } from 'src/plugins/parent-hub-stripe';
import * as fromStore from '../../../../store';
import * as slipPaymentIntentActions from '../../../../store/actions/slip-payment-intent.action';
import { EditPaymentAmountModalComponent } from '../../../edit-payment-amount-modal/edit-payment-amount-modal.component';
import { SlipComponentInfo } from '../../student-slip-item-choices-modal/student-slip-item-choices-modal.store';
import { ItemChoicesComponentInfo, PaymentStatus } from '../../student-slip-modal.store';

export interface OperationState<T> {
  loading: boolean;
  result: T;
}

export interface PaymentMethod {
  methodId: string;
  cardBrand: string;
  cardLast4: string;
}

class ProgressError extends Error {
  constructor(
    message: string,
    public canRetry: boolean
  ) {
    super(message);
  }
}

export interface FormState {
  paymentStatus: PaymentStatus;
  slipComponentInfo: SlipComponentInfo;
  itemChoicesComponentInfo: ItemChoicesComponentInfo;
  payNowAmount: number;
  paymentMethod: PaymentMethod;
  intentState: OperationState<CreateIntentResponse>;
  progressError: ProgressError;
  ready: boolean;
  legend: string;
  card: any;
  cardError: string;
  canEditAmount: boolean;
  reservationExpiresOn: string;
  refreshReservationEmitter: EventEmitter<void>;
  paymentRequestButtonId: string;
}

@Injectable()
export class StudentSlipItemChoicesPaymentWebStore extends ComponentStore<FormState> {
  constructor(
    private store: Store,
    private slipService: SlipService,
    private modalCtrl: ModalController
  ) {
    super({
      paymentStatus: null,
      slipComponentInfo: null,
      itemChoicesComponentInfo: null,
      payNowAmount: null,
      paymentMethod: null,
      intentState: null,
      progressError: null,
      ready: false,
      legend: 'Complete',
      card: null,
      cardError: null,
      canEditAmount: false,
      reservationExpiresOn: null,
      refreshReservationEmitter: null,
      paymentRequestButtonId: null
    });
  }

  readonly vm$ = this.select(state => state).pipe(tap(vm => console.log('Web form vm$ ', vm)));
  readonly paymentStatus$ = this.select(state => state.paymentStatus).pipe(
    tap(paymentStatus => console.log('Web form paymentStatus$ ', paymentStatus))
  );

  private createIntent(): Observable<CreateIntentResponse> {
    this.patchState({ intentState: { loading: true, result: null } });
    return this.slipService
      .createSlipItemChoicesIntent(
        this.get().slipComponentInfo.schoolId,
        this.get().slipComponentInfo.studentId,
        this.get().slipComponentInfo.slipId,
        this.get().slipComponentInfo.componentId,
        this.get().payNowAmount
      )
      .pipe(
        tap((result) => {
          this.patchState({ intentState: { loading: false, result: result } });
        }),
        catchError((error) => {
          throw getIntentError(error);
        })
      );
  }

  readonly payNow = this.effect((params$: Observable<{ stripe: any }>) => {
    return params$.pipe(
      switchMap((params) => {
        const expired = this.get().reservationExpiresOn && moment().isAfter(moment(this.get().reservationExpiresOn));
        if (expired) {
          this.get().refreshReservationEmitter.emit();
          return EMPTY;
        }
        this.patchState({ paymentStatus: { inProgress: true, method: 'card', intentId: null }, progressError: null });
        return combineLatest([
          timer(1000),
          this.createIntent().pipe(
            switchMap(intent =>
              from(
                params.stripe.handleCardPayment(intent.intentSecretKey, this.get().card, {
                  save_payment_method: false
                })
              ).pipe(
                map(() => ({ intent: intent })),
                tap((result: any) => {
                  if (result.error) throw new ProgressError(result.error.message, true);
                })
              )
            )
          )
        ]).pipe(
          tapResponse(
            ([_, result]) => {
              this.patchState({
                paymentStatus: { inProgress: false, method: 'card', intentId: result.intent.intentId }
              });

              this.store.dispatch(
                slipPaymentIntentActions.addIntent({
                  intent: {
                    id: result.intent.intentId,
                    version: 2,
                    tenant: this.get().slipComponentInfo.schoolId,
                    studentId: this.get().slipComponentInfo.studentId,
                    slipId: this.get().slipComponentInfo.slipId,
                    method: 'card',
                    nextCheckTimestamp: 0,
                    attempt: 0
                  }
                })
              );
            },
            (error: ProgressError) => {
              this.patchState({
                paymentStatus: null,
                progressError: error
              });
            }
          )
        );
      })
    );
  });

  readonly editAmount = this.effect((trigger$) => {
    return trigger$.pipe(
      tap(async () => {
        const state = this.get();
        const operation = (amount: number): Observable<CreateIntentResponse> => {
          // Create dummy response since we now fetch intent prior to starting payment action
          return of({
            intentId: null,
            intentSecretKey: null,
            ephemeralKey: null,
            amount: amount
          });
        };
        const modal = await this.modalCtrl.create({
          component: EditPaymentAmountModalComponent,
          componentProps: {
            paymentTitle: state.slipComponentInfo.title,
            balance: state.itemChoicesComponentInfo.balance,
            minimumAmount: state.itemChoicesComponentInfo.minimumAmount,
            intentRequest: operation
          },
          backdropDismiss: false
        });
        await modal.present();
        const { data } = await modal.onWillDismiss();
        if (data) {
          const intentResponse = data.intentResponse as CreateIntentResponse;
          this.patchState({ payNowAmount: intentResponse.amount });
        }
      })
    );
  });

  readonly updatePaymentMethod = this.effect((params$: Observable<{ methodId: string }>) => {
    return params$.pipe(
      tap((params: { methodId: string }) => {
        this.store.dispatch(new fromStore.UpdatePaymentMethodId({ paymentMethodId: params.methodId }));
      })
    );
  });

  readonly destroyCard = this.effect((trigger$) => {
    return trigger$.pipe(
      tap(() => {
        this.get().card.destroy();
        this.patchState({ card: null });
      })
    );
  });

  readonly initialiseStripe = this.effect(
    (params$: Observable<{ stripe: any; cardInfo: any; paymentRequestButtonId: string }>) => {
      return params$.pipe(
        tap((params: { stripe: any; cardInfo: any; paymentRequestButtonId: string }) => {
          const elements = params.stripe.elements();
          const style = {
            base: {
              'color': '#32325d',
              'lineHeight': '24px',
              'fontFamily': '"Helvetica Neue", Helvetica, sans-serif',
              'fontSmoothing': 'antialiased',
              'fontSize': '16px',
              '::placeholder': {
                color: '#aab7c4'
              }
            },
            invalid: {
              color: '#e93423',
              iconColor: '#e93423'
            }
          };
          const card = elements.create('card', { style });
          this.patchState({ card: card, paymentRequestButtonId: params.paymentRequestButtonId });
          card.mount(params.cardInfo.nativeElement);

          card.on('change', ({ error }) => {
            this.patchState({ cardError: error?.message ?? null });
          });

          const paymentRequest = params.stripe.paymentRequest({
            country: 'GB',
            currency: 'gbp',
            total: {
              label: this.get().slipComponentInfo.title,
              amount: this.get().payNowAmount
            },
            requestPayerName: true,
            requestPayerEmail: true
          });

          const prButton = elements.create('paymentRequestButton', {
            paymentRequest
          });

          paymentRequest.canMakePayment().then((result) => {
            if (result) {
              prButton.mount(`#${this.get().paymentRequestButtonId}`);
              this.patchState({ legend: 'Or enter' });
            }
            this.patchState({ ready: true });
          });

          paymentRequest.on('paymentmethod', (ev) => {
            const expired =
              this.get().reservationExpiresOn && moment().isAfter(moment(this.get().reservationExpiresOn));
            if (expired) {
              this.get().refreshReservationEmitter.emit();
            } else {
              this.patchState({ paymentStatus: { inProgress: true, method: 'card', intentId: null } });
              params.stripe
                .confirmCardPayment(
                  this.get().intentState.result.intentSecretKey,
                  { payment_method: ev.paymentMethod.id },
                  { handleActions: false }
                )
                .then((confirmResult) => {
                  if (confirmResult.error) {
                    // Report to the browser that the payment failed, prompting it to
                    // re-show the payment interface, or show an error message and close
                    // the payment interface.
                    ev.complete('fail');
                  } else {
                    // Report to the browser that the confirmation was successful, prompting
                    // it to close the browser payment method collection interface.
                    ev.complete('success');
                    // Let Stripe.js handle the rest of the payment flow.
                    params.stripe.confirmCardPayment(this.get().intentState.result.intentSecretKey).then((result) => {
                      if (result.error) {
                        // The payment failed -- ask your customer for a new payment method.
                      } else {
                        // The payment has succeeded.
                        this.patchState({
                          paymentStatus: {
                            inProgress: false,
                            method: 'card',
                            intentId: this.get().intentState.result.intentId
                          }
                        });
                        this.store.dispatch(
                          slipPaymentIntentActions.addIntent({
                            intent: {
                              id: result.intent.intentId,
                              version: 2,
                              tenant: this.get().slipComponentInfo.schoolId,
                              studentId: this.get().slipComponentInfo.studentId,
                              slipId: this.get().slipComponentInfo.slipId,
                              method: 'card',
                              nextCheckTimestamp: 0,
                              attempt: 0
                            }
                          })
                        );
                      }
                    });
                  }
                });
            }
          });
        })
      );
    }
  );

  readonly chooseMethod = this.effect((trigger$) => {
    return trigger$.pipe(
      tap(() => {
        ParentHubStripe.choosePaymentMethod({
          paymentMethodId: this.get().itemChoicesComponentInfo.paymentMethodId
        }).catch((error) => {
          console.log(error);
        });
      })
    );
  });
}
function getIntentError(httpError: HttpErrorResponse): ProgressError {
  const intentErrors: { [code: number]: { message: string; canRetry: boolean } } = {
    2000: {
      message: 'This slip has been closed by the school. Making payment online is no longer available.',
      canRetry: false
    },
    2001: {
      message: 'This slip is no longer active for your child. Please contact the school for more information.',
      canRetry: false
    },
    2003: { message: 'Payment not possible. Please contact the school for more information.', canRetry: false },
    2004: {
      message: `Your payment couldn't be processed because the balance on the slip has already been paid in full.`,
      canRetry: false
    }
  };
  let errorResult;
  if (httpError.status === 0)
    errorResult = {
      message: `It looks like you're offline. Please check your connection and try again.`,
      canRetry: true
    };
  else if (httpError.status === 404)
    errorResult = {
      message: `We were unable to process your payment. Please contact support@parenthub.co.uk.`,
      canRetry: true
    };
  else if (httpError?.error?.ErrorCode) {
    const error = intentErrors[httpError.error.ErrorCode];
    errorResult = error ?? {
      message:
        `Payment not possible. Please contact support@parenthub.co.uk quoting error code: ` + httpError.error.ErrorCode,
      canRetry: true
    };
  } else
    errorResult = { message: `We weren't able to process your payment this time. Please try again.`, canRetry: true };
  return new ProgressError(errorResult.message, errorResult.canRetry);
}
